import type { Action } from "app/services/types.generated";
import { getStorageMapPreviewBmpUrl } from "lib/consts/stcmMapList";

export type NavYamlGeometry = {
    resolution: number;
    originX: number;
    originY: number;
    originYaw: number;
    /** Grid width in cells when present in stored filter YAML. */
    width?: number;
    /** Grid height in cells when present in stored filter YAML. */
    height?: number;
};

export type MissionLoadMapStep = {
    mapId: string;
    variantBmpRelPath?: string;
};

const NUMBER = String.raw`[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?`;

const finite = (value: number): boolean => Number.isFinite(value);

/**
 * Parse nav2-style filter YAML (`resolution` + `origin: [x, y, yaw]` or origin_x/y).
 * Returns null when resolution or origin is missing — callers must not invent 0,0 / 0.05.
 */
export function parseNavYamlGeometry(yaml: string | null | undefined): NavYamlGeometry | null {
    const text = (yaml ?? "").trim();
    if (!text) return null;

    const resolutionMatch = text.match(new RegExp(String.raw`^\s*resolution:\s*(${NUMBER})\s*$`, "m"));
    if (!resolutionMatch) return null;
    const resolution = Number(resolutionMatch[1]);
    if (!finite(resolution) || resolution <= 0) return null;

    const originList = text.match(
        new RegExp(
            String.raw`^\s*origin:\s*\[\s*(${NUMBER})\s*,\s*(${NUMBER})\s*(?:,\s*(${NUMBER}))?\s*\]\s*$`,
            "m",
        ),
    );
    let originX: number;
    let originY: number;
    let originYaw = 0;
    if (originList) {
        originX = Number(originList[1]);
        originY = Number(originList[2]);
        if (originList[3] != null) originYaw = Number(originList[3]);
    } else {
        const ox = text.match(new RegExp(String.raw`^\s*origin_x:\s*(${NUMBER})\s*$`, "m"));
        const oy = text.match(new RegExp(String.raw`^\s*origin_y:\s*(${NUMBER})\s*$`, "m"));
        const oyaw = text.match(new RegExp(String.raw`^\s*origin_yaw:\s*(${NUMBER})\s*$`, "m"));
        if (!ox || !oy) return null;
        originX = Number(ox[1]);
        originY = Number(oy[1]);
        if (oyaw) originYaw = Number(oyaw[1]);
    }
    if (!finite(originX) || !finite(originY) || !finite(originYaw)) return null;

    const widthMatch = text.match(new RegExp(String.raw`^\s*width:\s*(${NUMBER})\s*$`, "m"));
    const heightMatch = text.match(new RegExp(String.raw`^\s*height:\s*(${NUMBER})\s*$`, "m"));
    let width: number | undefined;
    let height: number | undefined;
    if (widthMatch) {
        const w = Number(widthMatch[1]);
        if (finite(w) && w > 0) width = Math.round(w);
    }
    if (heightMatch) {
        const h = Number(heightMatch[1]);
        if (finite(h) && h > 0) height = Math.round(h);
    }

    return {
        resolution,
        originX,
        originY,
        originYaw,
        ...(width != null ? { width } : {}),
        ...(height != null ? { height } : {}),
    };
}

export type MissionPreviewOverlayGridSource = "filter_yaml" | "preview_image";

/**
 * Leaflet overlay extent in occupancy cells.
 * Uses filter YAML width/height when stored; otherwise preview PNG pixels (1:1).
 */
export function missionPreviewOverlayGridCells(
    filterYaml: NavYamlGeometry,
    previewImagePx: { width: number; height: number },
): { width: number; height: number; source: MissionPreviewOverlayGridSource } {
    if (
        filterYaml.width != null &&
        filterYaml.height != null &&
        filterYaml.width > 0 &&
        filterYaml.height > 0
    ) {
        return {
            width: filterYaml.width,
            height: filterYaml.height,
            source: "filter_yaml",
        };
    }

    return {
        width: previewImagePx.width,
        height: previewImagePx.height,
        source: "preview_image",
    };
}

/** Log geometry used for mission preview alignment (PNG vs filter grid). */
export function logMissionMapPreviewSizes(
    context: string,
    details: {
        mapId: string;
        stcmRel?: string;
        variant?: string | null;
        previewImagePx: { width: number; height: number };
        filterYaml: NavYamlGeometry;
        overlayGridCells: { width: number; height: number };
        leafletBounds?: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } };
        overlayGridSource?: MissionPreviewOverlayGridSource;
    },
): void {
    const { filterYaml, previewImagePx, overlayGridCells } = details;
    const widthMeters = overlayGridCells.width * filterYaml.resolution;
    const heightMeters = overlayGridCells.height * filterYaml.resolution;
    const filterGrid =
        filterYaml.width != null && filterYaml.height != null
            ? { width: filterYaml.width, height: filterYaml.height }
            : null;
    const pngMatchesFilterGrid =
        filterGrid == null
            ? null
            : filterGrid.width === previewImagePx.width &&
              filterGrid.height === previewImagePx.height;
    const overlayUsesImageAsGrid =
        overlayGridCells.width === previewImagePx.width &&
        overlayGridCells.height === previewImagePx.height;

    console.info(`[MissionMapPreview] ${context}`, {
        mapId: details.mapId,
        stcmRel: details.stcmRel ?? null,
        variant: details.variant ?? null,
        previewImagePx,
        filterYamlGridCells: filterGrid,
        overlayGridCells,
        overlayUsesImageAsGrid,
        pngMatchesFilterGrid,
        resolution: filterYaml.resolution,
        origin: { x: filterYaml.originX, y: filterYaml.originY, yaw: filterYaml.originYaw },
        spanMetersFromOverlay: { width: widthMeters, height: heightMeters },
        boundsSw: { x: filterYaml.originX, y: filterYaml.originY },
        boundsNe: {
            x: filterYaml.originX + widthMeters,
            y: filterYaml.originY + heightMeters,
        },
        leafletBounds: details.leafletBounds ?? null,
        overlayGridSource: details.overlayGridSource ?? null,
    });
}

/** First LoadMap in mission sequence that has a map_id. */
export function firstLoadMapStep(actions: Action[]): MissionLoadMapStep | null {
    const loadMaps = actions
        .filter((action) => action.actionType === "LoadMap")
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    for (const action of loadMaps) {
        const args = (action.args ?? {}) as {
            map_id?: unknown;
            variant_bmp_rel_path?: unknown;
        };
        const mapId = typeof args.map_id === "string" ? args.map_id.trim() : "";
        if (!mapId) continue;
        const variant =
            typeof args.variant_bmp_rel_path === "string" ? args.variant_bmp_rel_path.trim() : "";
        return {
            mapId,
            ...(variant ? { variantBmpRelPath: variant } : {}),
        };
    }
    return null;
}

export function missionPreviewImageUrl(
    baseUrl: string,
    stcmRelPath: string,
    variantBmpRelPath?: string,
): string {
    const imagePath = variantBmpRelPath?.trim() || stcmRelPath;
    return getStorageMapPreviewBmpUrl(baseUrl, imagePath);
}
